import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import { State } from './types';

// Transform API data to frontend format
const transformAgentData = (apiData: any) => {
  if (!apiData) return [];

  return apiData.map((item: any) => ({
    agent_id: item.agent_id?.toString() || item.id?.toString(),
    name: item.name,
    email: item.email,
    phone: item.phone,
    whatsapp: item.whatsapp,
    address: item.address,
    country_code: item.country_code,
    agent_type: item.agent_type,
    website: item.website,
    telegram: item.telegram,
    description: item.description,
    logo: item.logo,
    status: item.status === 1 || item.status === true,
    position: item.position,
    social_links: item.social_links || [],
    created_at: item.created_at,
    updated_at: item.updated_at,
  }));
};

// Agents response interface
export interface AgentsResponse {
  message: string;
  status: string;
  data: any[];
  pagination?: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export const initialState: State = {
  selectedAgent: null,
};

const slice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    setSelectedAgent: (state, action: PayloadAction<any>) => {
      state.selectedAgent = action.payload;
    },
  },
});

export const api = createApi({
  reducerPath: 'agentsApi',
  baseQuery: baseQuery,
  tagTypes: ['Agents'],
  endpoints: builder => ({
    getAgents: builder.query<
      AgentsResponse,
      { page?: number; limit?: number; search?: string }
    >({
      query: params => ({
        url: endpoints.AGENTS,
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search || '',
        },
      }),
      transformResponse: (response: any) => {
        // The baseQuery wraps the actual API response in result.data
        const apiResponse = response.data || response;

        // Handle different response structures
        let data = apiResponse.data;
        let pagination = apiResponse.pagination;

        // If response is wrapped in a data property
        if (apiResponse.data && apiResponse.data.data) {
          data = apiResponse.data.data;
          pagination = apiResponse.data.pagination;
        }

        // Fallback: if data is directly in the response
        if (!data && Array.isArray(apiResponse)) {
          data = apiResponse;
        }

        const transformedData = transformAgentData(data);

        return {
          message: apiResponse.message || 'Success',
          status: apiResponse.status || 'success',
          data: transformedData,
          pagination: pagination || {
            current_page: 1,
            total_pages: 1,
            total_items: transformedData.length,
            items_per_page: transformedData.length,
            has_next: false,
            has_prev: false,
          },
        };
      },
      providesTags: ['Agents'],
    }),

    addAgent: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.AGENTS + '/create',
        method: 'POST',
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          whatsapp: data.whatsapp,
          address: data.address,
          country_code: data.country_code,
          agent_type: data.agent_type,
          website: data.website,
          telegram: data.telegram,
          description: data.description,
          status: data.status ? 1 : 0,
          logo: data.logo,
          social_links: data.social_links || [],
        },
      }),
      invalidatesTags: ['Agents'],
    }),

    updateAgent: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: endpoints.AGENTS + '/update/' + id,
        method: 'PUT',
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          whatsapp: data.whatsapp,
          address: data.address,
          country_code: data.country_code,
          agent_type: data.agent_type,
          website: data.website,
          telegram: data.telegram,
          description: data.description,
          status: data.status ? 1 : 0,
          logo: data.logo,
          social_links: data.social_links || [],
        },
      }),
      invalidatesTags: ['Agents'],
    }),

    deleteAgent: builder.mutation<any, string>({
      query: id => ({
        url: endpoints.AGENTS + '/delete/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['Agents'],
    }),

    changePosition: builder.mutation<
      any,
      Array<{ id: string; position: number }>
    >({
      query: positions => ({
        url: endpoints.AGENTS + '/change-position',
        method: 'PUT',
        body: { positions },
      }),
      invalidatesTags: ['Agents'],
    }),

    countriesAgents: builder.query({
      query: () => ({
        url: endpoints.AGENTS + '/countries',
        method: 'GET',
      }),
    }),
  }),
});

export const { actions: agentsAction } = slice;

export const useAgentsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    useGetAgentsQuery: api.useLazyGetAgentsQuery,
    useAddAgentMutation: api.useAddAgentMutation,
    useUpdateAgentMutation: api.useUpdateAgentMutation,
    useDeleteAgentMutation: api.useDeleteAgentMutation,
    useChangePositionMutation: api.useChangePositionMutation,
    useCountriesAgentsQuery: api.useLazyCountriesAgentsQuery,
  };
};
